// Inventory API client.
//
// Thin wrappers over the generated services client. Going through
// `ApiClient.serviceApi.inventory.*` is what attaches the bearer token: the
// generated HttpClient runs its `securityWorker` inside `request()`, and that is
// NOT an axios request interceptor on `.instance`. Calling `.instance.get(...)`
// directly sends an unauthenticated request and the backend answers 401.
//
// These wrappers exist only to keep the dashboard's camelCase call sites and to
// map them onto the endpoints' snake_case query/body params.

import { ApiClient } from '@api/api-configuration';
import type {
  InventoryItemCreate as GenInventoryItemCreate,
  InventoryItemUpdate as GenInventoryItemUpdate,
  InventoryMovementCreate as GenInventoryMovementCreate,
} from '@api/READ_ONLY/services_api/Api';

import type { InventoryItemCreate, InventoryItemUpdate, InventoryMovementCreate } from './types';

const inventory = () => ApiClient.serviceApi.inventory;

// The feature types mirror the generated string enums as string-literal unions
// (see types.ts) because the UI compares them against literals. The values are
// identical on the wire, so widening to the generated shape at this boundary is
// a naming difference only — TypeScript just won't accept a literal for an enum.
const asWire = <T>(body: unknown) => body as T;

// Categories
//
// `query` (free-text, >=3 chars) and `sort` (JSON:API, e.g. 'name,-created_at').
// Sort fields must be in SORTABLE_CATEGORY_FIELDS (see inventory_category.py) or
// the endpoint answers 400.
type ListCategoriesParams = { offset?: number; limit?: number; query?: string; sort?: string };

export const listCategories = (params?: ListCategoriesParams) =>
  inventory()
    .listInventoryCategories(params ?? {})
    .then((r) => r.data);

export const createCategory = (body: { name: string; description?: string }) =>
  inventory()
    .createInventoryCategory(body)
    .then((r) => r.data);

export const updateCategory = (id: number, body: { name?: string; description?: string }) =>
  inventory()
    .updateInventoryCategory(id, body)
    .then((r) => r.data);

export const deleteCategory = (id: number) =>
  inventory()
    .deleteInventoryCategory(id)
    .then((r) => r.data);

// Items
export const listItems = (params: {
  offset?: number;
  limit?: number;
  categoryId?: number;
  capability?: string;
  query?: string;
  sort?: string;
}) => {
  const { categoryId, ...rest } = params;
  return inventory()
    .listInventoryItems({ ...rest, ...(categoryId === undefined ? {} : { category_id: categoryId }) })
    .then((r) => r.data);
};

export const getItem = (id: number) =>
  inventory()
    .getInventoryItem(id)
    .then((r) => r.data);

export const getItemSummary = (id: number) =>
  inventory()
    .getInventoryItemSummary(id)
    .then((r) => r.data);

export const createItem = (body: InventoryItemCreate) =>
  inventory()
    .createInventoryItem(asWire<GenInventoryItemCreate>(body))
    .then((r) => r.data);

export const updateItem = (id: number, body: InventoryItemUpdate) =>
  inventory()
    .updateInventoryItem(id, asWire<GenInventoryItemUpdate>(body))
    .then((r) => r.data);

export const deleteItem = (id: number) =>
  inventory()
    .deleteInventoryItem(id)
    .then((r) => r.data);

// Movements
export const listMovements = (params: {
  offset?: number;
  limit?: number;
  itemId?: number;
  movementType?: string;
  recipientType?: string;
  recipientId?: number;
  visitId?: number;
}) => {
  const { itemId, movementType, recipientType, recipientId, visitId, ...rest } = params;
  return inventory()
    .listInventoryMovements({
      ...rest,
      ...(itemId === undefined ? {} : { item_id: itemId }),
      ...(movementType === undefined ? {} : { movement_type: movementType }),
      ...(recipientType === undefined ? {} : { recipient_type: recipientType }),
      ...(recipientId === undefined ? {} : { recipient_id: recipientId }),
      ...(visitId === undefined ? {} : { visit_id: visitId }),
    })
    .then((r) => r.data);
};

export const createMovement = (body: InventoryMovementCreate) =>
  inventory()
    .createInventoryMovement(asWire<GenInventoryMovementCreate>(body))
    .then((r) => r.data);

// Rentals & availability
//
// `query` (free-text, >=3 chars) searches the recipient and the rented item's name.
type ListRentalsParams = {
  status?: 'active' | 'overdue';
  offset?: number;
  limit?: number;
  query?: string;
  sort?: string;
};

export const listRentals = (params: ListRentalsParams) =>
  inventory()
    .listInventoryRentals(params)
    .then((r) => r.data);

export const returnRental = (rentalOutId: number, body: { actualReturn?: string }) =>
  inventory()
    .returnInventoryRental(rentalOutId, { actual_return: body.actualReturn ?? null })
    .then((r) => r.data);

export const getAvailability = (params: { item_id: number; from: string; to: string }) =>
  inventory()
    .getInventoryAvailability(params)
    .then((r) => r.data);
